'use client';
import { ThemeOptions, createTheme } from '@mui/material/styles';

// Color palette extracted from global.css and tailwind.config.ts
const colors = {
  // Text colors
  text: {
    primary: '#1b1d21',
    secondary: '#808191', // color-gray
    white: '#ffffff',
    green: '#7fba7a',
    red: '#ff754c',
    blue: '#ff8c19',
    blueDark: '#5f75ee',
    purple: '#6c5dd3',
    // Tailwind primary colors
    primaryOrange: '#FA941C',
    secondaryPurple: '#6F43F3',
    // 新增颜色
    success: '#7FBA7A',
    danger: '#FF754C',
  },
  // Background colors
  bg: {
    blue: '#ff8c19',
    blueOpacity: 'rgba(63, 140, 255, 0.3)',
    blueLight: '#a0d7e7',
    blueLightOpacity: 'rgba(160, 215, 231, 0.3)',
    yellow: '#ffce73',
    yellowOpacity: 'rgba(255, 206, 115, 0.3)',
    yellowLight: '#ffe2ac',
    yellowWarm: '#FFBC3E',
    pink: '#ffa2c0',
    pinkOpacity: 'rgba(255, 162, 192, 0.3)',
    pinkLight: '#ffebf6',
    green: '#7fba7a',
    greenOpacity: 'rgba(127, 186, 122, 0.3)',
    purple: '#6c5dd3',
    purpleOpacity: 'rgba(108, 93, 211, 0.3)',
    purpleLight: '#cfc8ff',
    red: '#ff754c',
    redOpacity: 'rgba(255, 117, 76, 0.3)',
    // Tailwind primary colors
    primaryOrange: '#FA941C',
    secondaryPurple: '#6F43F3',
    // 新增颜色
    success: '#7FBA7A',
    successOpacity: 'rgba(127, 186, 122, 0.3)',
    danger: '#FF754C',
    dangerOpacity: 'rgba(255, 117, 76, 0.3)',
  },
  // Widget specific colors
  widget: {
    background: {
      light: '#ffffff',
      dark: '#242731',
    },
    shadow: {
      light: '#e3e6ec',
      dark: '#000000',
    },
  },
  // Dark mode specific colors
  dark: {
    background: '#242731',
    card: '#242731',
    button: '#1f2128',
    buttonHover: '#1b1d21',
  },
};

// 只保留必要的全局样式
export const essentialGlobalStyles = {
  'html, body': {
    margin: 0,
    padding: 0,
    height: '100%',
    width: '100%',
    scrollBehavior: 'smooth',
  },
  body: {
    backgroundColor: colors.widget.background.dark,
    color: colors.text.white,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  '.page': {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
  },
  '.page__wrapper': {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
    paddingLeft: 'var(--sidebar-width, 256px)',
    transition: 'padding-left 0.3s ease',
    willChange: 'padding-left',
    '@media (max-width: 1023px)': {
      paddingLeft: '96px',
    },
  },
  // 高级现代化滚动条样式
  '*::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    transition: 'all 0.3s ease',
    zIndex: 40,
  },
  '*::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    margin: '4px 0',
    backdropFilter: 'blur(4px)',
    borderRadius: '10px',
  },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: `rgba(250, 148, 28, 0.5)`, // 提高不透明度
    borderRadius: '10px',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    backgroundImage: `linear-gradient(180deg, rgba(250, 148, 28, 0.7) 0%, rgba(111, 67, 243, 0.5) 100%)`, // 提高渐变色不透明度
    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundSize: '100% 100%',
    backgroundPosition: '0 0',
  },
  '*::-webkit-scrollbar-thumb:hover': {
    backgroundColor: `rgba(250, 148, 28, 0.8)`,
    backgroundImage: `linear-gradient(180deg, rgba(250, 148, 28, 0.9) 0%, rgba(111, 67, 243, 0.7) 100%)`,
    backgroundSize: '100% 200%',
    backgroundPosition: '0 100%',
    boxShadow: 'inset 0 0 8px rgba(250, 148, 28, 0.4), 0 0 10px rgba(250, 148, 28, 0.3)',
  },
  '*::-webkit-scrollbar-thumb:active': {
    backgroundColor: `rgba(250, 148, 28, 0.9)`,
    backgroundImage: `linear-gradient(180deg, rgba(250, 148, 28, 1) 0%, rgba(111, 67, 243, 0.8) 100%)`,
    transform: 'scaleX(0.95)',
    boxShadow: 'inset 0 0 10px rgba(250, 148, 28, 0.5), 0 0 15px rgba(250, 148, 28, 0.4)',
  },
  '*::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
  // Firefox滚动条样式
  html: {
    scrollbarWidth: 'auto',
    scrollbarColor: `rgba(250, 148, 28, 0.7) rgba(0, 0, 0, 0.05)`,
  },
  // 确保所有元素的滚动行为一致
  '*': {
    boxSizing: 'border-box',
    WebkitTapHighlightColor: 'transparent',
    '&::selection': {
      backgroundColor: 'rgba(250, 148, 28, 0.2)',
      color: colors.text.white,
    },
  },
  // 添加全局过渡效果
  'a, button, input, select, textarea, [tabindex]': {
    transition: 'all 0.2s ease',
    outline: 'none',
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${colors.bg.primaryOrange}40`,
    },
    '&:focus:not(:focus-visible)': {
      boxShadow: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${colors.bg.primaryOrange}40`,
    },
  },
  // 为侧边栏和内容区域添加过渡效果
  '.sidebar-transition': {
    '& .page__wrapper': {
      transitionProperty: 'padding-left',
      transitionDuration: '0.3s',
      transitionTimingFunction: 'ease',
    },
  },
};

// Custom theme extension for our specific components
declare module '@mui/material/styles' {
  interface Palette {
    customColors: typeof colors;
  }
  interface PaletteOptions {
    customColors: typeof colors;
  }
}

// Extend Button props to include 'white' color
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true;
  }
}

// Common theme options
const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: [
      'Poppins',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: 1.33333,
    },
    body1: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
    },
    body2: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
    },
    button: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          padding: 32,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          '&:last-child': {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 166,
          height: 56,
          padding: '0 20px',
          borderRadius: 16,
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          lineHeight: 1.42857,
          fontWeight: 700,
          transition: 'all 0.25s',
        },
        containedPrimary: {
          backgroundColor: colors.bg.primaryOrange,
          color: colors.text.white,
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: colors.bg.primaryOrange,
              opacity: 0.9,
            },
          },
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'white' },
          style: {
            backgroundColor: colors.text.white,
            color: colors.text.primaryOrange,
            boxShadow: '0 10px 20px rgba(227, 230, 236, 0.6)',
            '@media (hover: hover)': {
              '&:hover': {
                backgroundColor: colors.text.white,
                color: colors.text.primary,
              },
            },
          },
        },
      ],
    },
  },
};

// Dark theme (default for desktop)
export const desktopTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: colors.bg.primaryOrange,
    },
    secondary: {
      main: colors.bg.secondaryPurple,
    },
    text: {
      primary: colors.text.white,
      secondary: colors.text.secondary,
    },
    background: {
      default: colors.dark.background,
      paper: colors.dark.card,
    },
    customColors: colors,
  },
  components: {
    ...commonThemeOptions.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          padding: 32,
          backgroundColor: colors.dark.card,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 166,
          height: 56,
          padding: '0 20px',
          borderRadius: 16,
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          lineHeight: 1.42857,
          fontWeight: 700,
          transition: 'all 0.25s',
        },
        containedPrimary: {
          backgroundColor: colors.bg.primaryOrange,
          color: colors.text.white,
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: colors.bg.primaryOrange,
              opacity: 0.9,
            },
          },
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'white' },
          style: {
            backgroundColor: colors.dark.button,
            color: colors.text.white,
            '@media (hover: hover)': {
              '&:hover': {
                backgroundColor: colors.dark.buttonHover,
              },
            },
          },
        },
      ],
    },
  },
});

// Light theme (for web)
export const webTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.bg.primaryOrange,
    },
    secondary: {
      main: colors.bg.secondaryPurple,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    background: {
      default: '#f4f5f6',
      paper: colors.widget.background.light,
    },
    customColors: colors,
  },
});
